import { financeExplorerColumns, zeroBilateralPnlCell, type FinanceComparisonGroupNode, type FinanceComparisonProjection,
  type FinanceComparisonRow, type FinanceComparisonSource, type FinanceExplorerValues } from "@chro/shared";

type Leg = NonNullable<FinanceComparisonSource["economics"]["legs"]>[number];
export type FinanceMatchedResultNode = Omit<Extract<FinanceComparisonGroupNode, { kind: "source" }>, "kind" | "children"> & {
  kind: "result";
  children: FinanceResultDisplayNode[];
  ownerRowId: string;
  legs: Leg[];
  quantity: number | null;
  unit: string | null;
  contract: string | null;
  pairLabel: string | null;
};
interface DisplayGroup extends Omit<Extract<FinanceComparisonGroupNode, { kind: "group" }>, "children"> { children: FinanceResultDisplayNode[] }
interface DisplaySource extends Omit<Extract<FinanceComparisonGroupNode, { kind: "source" }>, "children"> { children: FinanceResultDisplayNode[] }
export type FinanceResultDisplayNode = DisplayGroup | DisplaySource | Extract<FinanceComparisonGroupNode, { kind: "reference" }> | FinanceMatchedResultNode;

const sum = (values: (number | null)[]): number | null => values.some(value => value === null)
  ? null : values.reduce<number>((n, value) => n + Math.round(value! * 100), 0) / 100;
const values = (items: FinanceComparisonSource[]): FinanceExplorerValues => Object.fromEntries(financeExplorerColumns.map(({ key }) =>
  [key, sum(items.map(item => item.displayedValues[key]))])) as FinanceExplorerValues;
const nonzeroPnl = (row: FinanceComparisonRow) => Object.values(row.amountsByMonth).some(value => value !== 0);
const hasBilateral = (row: FinanceComparisonRow) => Object.values(row.bilateralByMonth).some(cell =>
  JSON.stringify(cell) !== JSON.stringify(zeroBilateralPnlCell()));

/** Only explicit engine matches are grouped. Sharing a product, PAIR label or
 * trade ID is not enough: each entry/exit/contract combination stays separate.
 * Schedule IDs and recognition months are intentionally not part of identity. */
function matchKey(item: FinanceComparisonSource): string | null {
  const entry = item.economics.legs?.find(leg => leg.role === "entry");
  const exit = item.economics.legs?.find(leg => leg.role === "exit");
  if (item.economics.recognitionKind !== "close" || !entry?.tradeId || !exit?.tradeId || entry.tradeId === exit.tradeId) return null;
  const identity = (leg: Leg) => [leg.tradeId, leg.contract, leg.side, leg.price, leg.date];
  return JSON.stringify([item.currency, item.book, item.trader, item.pair, item.contract, identity(entry), identity(exit)]);
}

function sourceRow(owner: FinanceComparisonRow, id: string, itemsByMonth: Record<string, FinanceComparisonSource[]>,
  months: string[], label: string): FinanceComparisonRow {
  const valuesByMonth = Object.fromEntries(months.map(month => [month, values(itemsByMonth[month] ?? [])]));
  const rangeValues = Object.fromEntries(financeExplorerColumns.map(({ key }) => [key,
    key === "openingCarry" ? valuesByMonth[months[0]!]!.openingCarry : key === "closingCarry" ? valuesByMonth[months.at(-1)!]!.closingCarry
      : sum(months.map(month => valuesByMonth[month]![key]))])) as FinanceExplorerValues;
  return { ...owner, id, label, valuesByMonth, rangeValues,
    amountsByMonth: Object.fromEntries(months.map(month => [month, valuesByMonth[month]!.recognizedPnl])), totalAmount: rangeValues.recognizedPnl,
    itemIdsByMonth: Object.fromEntries(months.map(month => [month, (itemsByMonth[month] ?? []).map(item => item.id)])),
    bilateralByMonth: Object.fromEntries(months.map(month => [month, zeroBilateralPnlCell()])), bilateralTotal: zeroBilateralPnlCell() };
}

/** A presentation and drill-down index over already rounded, ownership-filtered
 * sources. Canonical rows/totals/export inputs are never replaced or reallocated.
 * Additional rows are used ONLY for selecting a matched result's exact evidence. */
export function buildFinanceMatchedResults(view: FinanceComparisonProjection) {
  const resultNodes = new Map<string, FinanceMatchedResultNode[]>();
  const remainderRows = new Map<string, FinanceComparisonRow>();
  const extraRows: FinanceComparisonRow[] = [];
  const matchedTradeIds = new Set<string>();
  const openTradeIds = new Set<string>();
  const openQuantities = new Map<string, { quantity: number; unit: string | null }[]>();
  const byMonth = Object.fromEntries(view.months.map(month => [month, new Map(view.sourceItemsByMonth[month]!.map(item => [item.id, item]))]));
  if (view.metric !== "recognizedPnl") return { evidenceView: view, resultNodes, remainderRows, matchedTradeIds, openTradeIds, openQuantities };
  for (const item of view.sourceItemsByMonth[view.months.at(-1)!] ?? []) {
    if (item.economics.quantityRole !== "closing_position" || !item.tradeId || !item.economics.quantity) continue;
    openTradeIds.add(item.tradeId);
    const quantities = openQuantities.get(item.tradeId) ?? [];
    quantities.push({ quantity: item.economics.quantity, unit: item.economics.unit });
    openQuantities.set(item.tradeId, quantities);
  }
  for (const owner of view.rows) {
    if (owner.kind !== "trade" && owner.kind !== "pair") continue;
    const groups = new Map<string, Record<string, FinanceComparisonSource[]>>();
    const remainder: Record<string, FinanceComparisonSource[]> = {};
    for (const month of view.months) {
      remainder[month] = [];
      for (const id of owner.itemIdsByMonth[month] ?? []) {
        const item = byMonth[month]!.get(id);
        if (!item) continue;
        const key = matchKey(item);
        if (!key) { remainder[month]!.push(item); continue; }
        const group = groups.get(key) ?? {};
        (group[month] ??= []).push(item);
        groups.set(key, group);
      }
    }
    for (const [key, itemsByMonth] of groups) {
      const items = Object.values(itemsByMonth).flat();
      // A transfer/timing reference alone is not evidence of a recognized match.
      if (!items.some(item => item.economics.quantityRole === "recognition" || item.kind === "physical_recognition")) {
        for (const [month, sources] of Object.entries(itemsByMonth)) remainder[month]!.push(...sources);
        continue;
      }
      const physical = items.filter(item => item.kind === "physical_recognition");
      const recognized = items.filter(item => item.economics.quantityRole === "recognition");
      const quantities = (physical.length ? physical : recognized).map(item => item.economics.quantity);
      const units = [...new Set(items.map(item => item.economics.unit))];
      const quantity = quantities.length && quantities.every(quantity => quantity !== null) && units.length === 1
        ? quantities.reduce<number>((n, quantity) => n + quantity!, 0) : null;
      const sample = (physical[0] ?? recognized[0])!;
      const legs = sample.economics.legs!.filter(leg => leg.role === "entry" || leg.role === "exit");
      const id = `matched:${JSON.stringify([owner.id, key])}`;
      const label = `Matched result · ${sample.contract ?? "Contract unavailable"}`;
      const row = sourceRow(owner, id, itemsByMonth, view.months, label);
      row.sourceTradeIds = [...new Set(legs.map(leg => leg.tradeId!))];
      row.sourceTrades = row.sourceTradeIds.map(tradeId => owner.sourceTrades.find(trade => trade.tradeId === tradeId)
        ?? { tradeId, product: null, period: null, date: legs.find(leg => leg.tradeId === tradeId)?.date ?? null });
      row.sourceTradeDates = row.sourceTrades.map(trade => ({ tradeId: trade.tradeId, date: trade.date }));
      row.books = [sample.book]; row.traders = [sample.trader];
      row.products = [...new Set(row.sourceTrades.flatMap(trade => trade.product ? [trade.product] : []))];
      row.periods = [...new Set(row.sourceTrades.flatMap(trade => trade.period ? [trade.period] : []))];
      row.isMultiProduct = row.products.length > 1;
      row.sourceDeals = []; row.sourceDealIds = [];
      const node: FinanceMatchedResultNode = { ...row, kind: "result", id: `result:${id}`, row, label, ownerRowId: owner.id,
        children: [], rowIds: [id], targetRowId: id, canonicalPath: [], sourceCount: 1, referenceCount: 0,
        legs, quantity, unit: units.length === 1 ? units[0]! : null, contract: sample.contract, pairLabel: sample.pair };
      const nodes = resultNodes.get(owner.id) ?? [];
      nodes.push(node); resultNodes.set(owner.id, nodes); extraRows.push(row);
      legs.forEach(leg => matchedTradeIds.add(leg.tradeId!));
    }
    if (resultNodes.has(owner.id)) {
      const row = sourceRow(owner, `remaining:${owner.id}`, remainder, view.months, owner.label);
      row.bilateralByMonth = owner.bilateralByMonth; row.bilateralTotal = owner.bilateralTotal;
      remainderRows.set(owner.id, row); extraRows.push(row);
    }
  }
  return { evidenceView: { ...view, rows: [...view.rows, ...extraRows] }, resultNodes, remainderRows, matchedTradeIds, openTradeIds, openQuantities };
}

export type FinanceMatchedResults = ReturnType<typeof buildFinanceMatchedResults>;

export function groupFinanceMatchedResults(nodes: FinanceComparisonGroupNode[], matches: FinanceMatchedResults, compare: boolean,
  sortBy = "impact", direction: "asc" | "desc" = "desc"): FinanceResultDisplayNode[] {
  const sort = (nodes: FinanceResultDisplayNode[]) => nodes.sort((a, b) => {
    if (a.kind === "reference" || b.kind === "reference") return Number(a.kind === "reference") - Number(b.kind === "reference");
    const tie = a.label.localeCompare(b.label) || a.id.localeCompare(b.id);
    if (sortBy === "label") return (direction === "asc" ? 1 : -1) * tie;
    const value = (node: Exclude<FinanceResultDisplayNode, { kind: "reference" }>) => sortBy === "total" ? node.totalAmount
      : sortBy === "impact" ? Object.values(node.amountsByMonth).some(value => value === null) ? null
        : Math.max(0, ...Object.values(node.amountsByMonth).map(value => Math.abs(value!))) : node.amountsByMonth[sortBy] ?? null;
    const left = value(a), right = value(b);
    return left === null ? right === null ? tie : 1 : right === null ? -1 : (direction === "asc" ? 1 : -1) * (left - right) || tie;
  });
  const output = nodes.flatMap((node): FinanceResultDisplayNode[] => {
    if (node.kind === "group") {
      const children = groupFinanceMatchedResults(node.children, matches, compare, sortBy, direction);
      return children.length ? [{ ...node, children,
        label: node.dimension === "kind" && node.value === "trade" ? "No PAIR label" : node.label }] : [];
    }
    if (node.kind === "reference") return [node];
    const results = matches.resultNodes.get(node.row.id);
    const remainder = matches.remainderRows.get(node.row.id) ?? node.row;
    const hasOpen = (node.row.tradeId ? [node.row.tradeId] : node.row.sourceTradeIds).some(id => matches.openTradeIds.has(id));
    const linked = node.row.tradeId != null && matches.matchedTradeIds.has(node.row.tradeId);
    const keepRemainder = !compare || nonzeroPnl(remainder) || hasBilateral(remainder) || hasOpen || (!results && !linked);
    const remainingNode = { ...node, id: `source:${remainder.id}`, children: [], row: remainder, rowIds: [remainder.id], targetRowId: remainder.id,
      valuesByMonth: remainder.valuesByMonth, rangeValues: remainder.rangeValues, amountsByMonth: remainder.amountsByMonth, totalAmount: remainder.totalAmount };
    if (!results) return keepRemainder ? [remainingNode] : [];
    const children = sort([...results, ...(keepRemainder ? [remainingNode] : [])]);
    if (node.row.kind === "pair") return [{ ...node, children }];
    return children;
  });
  return output.some(node => node.kind === "result") ? sort(output) : output;
}
